import React from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Users,
  Package,
  AlertTriangle,
  RotateCcw,
  ArrowUpRight,
  BarChart3,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { adminStats, salesByCategory, salesByCondition, orders, products } from '../../data/mock';

const AdminDashboard = () => {
  const kpiCards = [
    {
      title: 'GMV (This Month)',
      value: `$${(adminStats.gmv / 1000).toFixed(1)}K`,
      change: adminStats.gmvGrowth,
      icon: DollarSign,
      color: 'emerald',
    },
    {
      title: 'Orders Today',
      value: adminStats.ordersToday,
      change: adminStats.ordersGrowth,
      icon: ShoppingCart,
      color: 'blue',
    },
    {
      title: 'Conversion Rate',
      value: `${adminStats.conversionRate}%`,
      change: adminStats.conversionGrowth,
      icon: TrendingUp,
      color: 'purple',
    },
    {
      title: 'Avg Order Value',
      value: `$${adminStats.avgOrderValue.toFixed(2)}`,
      change: adminStats.aovGrowth,
      icon: BarChart3,
      color: 'amber',
    },
    {
      title: 'Therapy Attach Rate',
      value: `${adminStats.attachRate}%`,
      change: adminStats.attachGrowth,
      icon: Users,
      color: 'cyan',
    },
    {
      title: 'Return Rate',
      value: `${adminStats.returnRate}%`,
      change: adminStats.returnGrowth,
      icon: RotateCcw,
      color: 'rose',
    },
  ];

  const recentOrders = orders.slice(0, 5);
  const lowStockProducts = products.filter((p) => p.stock < 50).slice(0, 5);
  const topProducts = [...products].sort((a, b) => b.reviewCount - a.reviewCount).slice(0, 5);

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-50 text-emerald-600',
      blue: 'bg-blue-50 text-blue-600',
      purple: 'bg-purple-50 text-purple-600',
      amber: 'bg-amber-50 text-amber-600',
      cyan: 'bg-cyan-50 text-cyan-600',
      rose: 'bg-rose-50 text-rose-600',
    };
    return colors[color] || colors.blue;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-emerald-100 text-emerald-700';
      case 'shipped':
        return 'bg-blue-100 text-blue-700';
      case 'processing':
        return 'bg-amber-100 text-amber-700';
      case 'packed':
        return 'bg-purple-100 text-purple-700';
      case 'pending':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-gray-500">Welcome back! Here's what's happening with your store.</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>Last updated: Just now</span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiCards.map((kpi, index) => (
          <Card key={index} className="border-gray-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getColorClasses(kpi.color)}`}>
                  <kpi.icon className="w-5 h-5" />
                </div>
                <div className={`flex items-center gap-1 text-sm font-medium ${
                  kpi.change >= 0 ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {kpi.change >= 0 ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  {Math.abs(kpi.change)}%
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
              <p className="text-sm text-gray-500">{kpi.title}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Alert Banner */}
      {adminStats.stockOutRisk > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">
                {adminStats.stockOutRisk} products are at risk of stock-out
              </p>
              <p className="text-sm text-amber-600">Review inventory levels and reorder soon.</p>
            </div>
            <a href="/admin/inventory" className="text-amber-700 font-medium text-sm hover:underline flex items-center gap-1">
              View Products <ArrowUpRight className="w-4 h-4" />
            </a>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sales by Category */}
        <Card className="border-gray-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Sales by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {salesByCategory.map((category, index) => {
                const maxValue = Math.max(...salesByCategory.map((c) => c.value));
                const percentage = (category.value / maxValue) * 100;
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{category.name}</span>
                      <span className="text-sm text-gray-500">${(category.value / 1000).toFixed(1)}K</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-red-500 to-rose-500 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Sales by Condition */}
        <Card className="border-gray-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Sales by Condition Tag</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {salesByCondition.map((condition, index) => {
                const maxValue = Math.max(...salesByCondition.map((c) => c.value));
                const percentage = (condition.value / maxValue) * 100;
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{condition.name}</span>
                      <span className="text-sm text-gray-500">${(condition.value / 1000).toFixed(1)}K</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <Card className="border-gray-100 lg:col-span-2">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Orders</CardTitle>
            <a href="/admin/orders" className="text-sm text-red-600 hover:underline flex items-center gap-1">
              View All <ArrowUpRight className="w-4 h-4" />
            </a>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Order ID</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Customer</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Amount</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => (
                    <tr key={order.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-2">
                        <span className="font-medium text-gray-900">{order.id}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-gray-600">{order.customerName}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="font-medium text-gray-900">${order.total.toFixed(2)}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card className="border-gray-100">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Top Products</CardTitle>
            <a href="/admin/products" className="text-sm text-red-600 hover:underline flex items-center gap-1">
              View All <ArrowUpRight className="w-4 h-4" />
            </a>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topProducts.map((product, index) => (
                <div key={product.id} className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-500">
                    {index + 1}
                  </span>
                  <div className="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={product.images?.[0] || 'https://placehold.co/40x40'}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                    <p className="text-xs text-gray-500">{product.reviewCount} sales</p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">${product.price.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <Card className="border-gray-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-500" />
              Low Stock Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-5 gap-4">
              {lowStockProducts.map((product) => (
                <div key={product.id} className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                  <div className="w-12 h-12 bg-white rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={product.images?.[0] || 'https://placehold.co/48x48'}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                    <p className="text-xs text-amber-600 font-medium">{product.stock} left</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;